import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { ContentQualityForm } from '@/settings/content-quality/form'
import { summariseThresholds } from '@/settings/content-quality/summary'
import { may, useActiveTenant } from '@/tenant/context'
import { SettingRow } from '@/ui/SettingRow'
import { ErrorState, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { useOneEditing } from '@/ui/useOneEditing'

/**
 * Settings › SEO › Content quality.
 *
 * *Where the gate is set* on `/seo/content` until the settings consolidation's
 * second batch (2026-09-15); that page shows the line and links here. Changing
 * the gate is `tenant:update`, as the pipeline page had it: setting an
 * editorial standard spends nothing, unlike the runs beside it.
 */
export function ContentQualityPanel(): JSX.Element {
  const { membership } = useActiveTenant()
  const editable = may(membership, 'tenant:update')
  const toast = useToast()
  const one = useOneEditing()
  const [savedAt, setSavedAt] = useState<string | null>(null)

  const limits = useQuery(
    (signal) => call(() => api.GET('/api/v1/seo/content/quality', { signal })),
    [],
  )
  const save = useMutation((body: Schema<'QualityThresholdsIn'>) =>
    call(() => api.PUT('/api/v1/seo/content/quality', { body })),
  )

  if (limits.state.status === 'loading')
    return <Loading label="Loading your quality gate" lines={2} />
  if (limits.state.status === 'error') {
    return (
      <ErrorState
        failure={limits.state.failure}
        title="Could not load your quality gate"
        onRetry={limits.refetch}
      />
    )
  }
  const stored = limits.state.data

  return (
    <div className="ar-stack">
      <h2 className="ar-h2">Content quality</h2>
      <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
        One editorial standard for the whole organisation. A draft that fails any of these never
        reaches the approval queue.
      </p>
      <SettingRow
        label="Quality gate"
        summary={summariseThresholds(stored)}
        savedAt={savedAt}
        editable={editable}
        editing={one.editing === 'gate'}
        onEdit={() => {
          one.open('gate')
        }}
        onCancel={one.close}
      >
        <ContentQualityForm
          stored={stored}
          submitLabel="Save"
          onSubmit={async (body) => {
            const result = await save.run(body)
            if (result.ok) {
              setSavedAt(new Date().toISOString())
              one.close()
              limits.refetch()
              toast.show({
                kind: 'success',
                title: 'Quality gate changed',
                body: 'It applies to the next draft judged. Press Run the gate again on a blocked one to re-judge it against this.',
              })
            }
            return result
          }}
        />
      </SettingRow>
    </div>
  )
}
